import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as React from 'react';

import { cn } from '@base/lib/utils';
import { setupForDark } from '@theme';

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { 'data-testid'?: string }
>(({ className, 'data-testid': dataTestId = 'tabsList', ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    data-testid={dataTestId}
    className={cn(
      'inline-flex h-10 items-center justify-center rounded-xxs p-1 w-full gap-0',
      setupForDark('bg-primary'),
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> {
  'data-testid'?: string;
  icon?: React.ReactNode;
  label?: string;
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(
  (
    { className, 'data-testid': dataTestId = 'tabsTrigger', icon, label, children, ...props },
    ref
  ) => {
    const displayLabel = label || (typeof children === 'string' ? children : '');
    return (
      <TabsPrimitive.Trigger
        ref={ref}
        data-testid={dataTestId}
        className={cn(
          'inline-flex flex-1 items-center justify-center whitespace-nowrap px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 gap-2 h-full min-h-0 min-w-0',
          'rounded-none border-0 outline-none',
          'first:rounded-l-xss first:rounded-tr-none first:rounded-br-none',
          'last:rounded-r-xss last:rounded-tl-none last:rounded-bl-none',
          'data-[state=active]:bg-violet-700 data-[state=active]:text-button1',
          'data-[state=inactive]:text-secondary data-[state=inactive]:bg-transparent',
          'dark:ring-offset-neutral-950 dark:focus-visible:ring-neutral-300',
          setupForDark('data-[state=active]:bg-violet-700'),
          setupForDark('data-[state=active]:text-button1'),
          setupForDark('data-[state=inactive]:text-secondary'),
          setupForDark('data-[state=inactive]:bg-transparent'),
          className
        )}
        {...props}
      >
        {icon && (
          <span className='shrink-0 w-6 h-6 flex items-center justify-center [&_svg]:fill-current [&_svg]:text-current'>
            {icon}
          </span>
        )}
        {displayLabel && <span>{displayLabel}</span>}
        {typeof children !== 'string' && children}
      </TabsPrimitive.Trigger>
    );
  }
);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn('mt-2 focus-visible:outline-none', className)}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsContent, TabsList, TabsTrigger };
